import { Injectable } from '@angular/core'

import { Observable, Subject, from, tap, throwError } from 'rxjs'

import {
  FILTER_ERRORS,
  FILTER_TYPES,
  Operators,
  ViewTasksFilter,
  fetchBulkAssignees,
  fetchTaskAssignees,
  fetchTaskComments,
  fetchTaskHistory,
  fetchViewTask,
  fetchViewTasks,
  formatView,
  getTaskManagerResources,
  initSseConnection,
  removeComment,
  saveComment,
  updateBulkTaskAction,
  updateTaskAction,
  updateTaskPriority,
} from '@flowx/core-sdk'

import {
  TMEnumerations,
  TMExecuteTaskActions,
  TMFilterOperators,
  TMFilters,
  TMPaginatedResponse,
  TMSetupOptions,
  TMSort,
  TMTaskComment,
  TMTaskHistory,
  TMUser,
  TMView,
  TMViewTask,
} from '../public.dictionary'

@Injectable()
export class PublicTasksService {
  #view: TMView
  #enumerations: TMEnumerations
  #filterOperators: TMFilterOperators
  #sseConnection = null
  #sseEvents = new Subject<MessageEvent<string>>()

  getTaskManagerResources(initConfig: TMSetupOptions): Observable<{
    view: TMView
    enumerations: TMEnumerations
    filterOperators: TMFilterOperators
  }> {
    return from(getTaskManagerResources(initConfig)).pipe(
      tap(({ view, enumerations, filterOperators }) => {
        this.setView(view)
        this.setEnumerations(enumerations)
        this.setFilterOperators(filterOperators)
      })
    )
  }

  setView(view: TMView, format = false): void {
    this.#view = format ? formatView(view) : view
  }

  getEnumerations(): TMEnumerations {
    return this.#enumerations
  }

  getView(): TMView {
    return this.#view
  }

  setEnumerations(enumerations: TMEnumerations): void {
    this.#enumerations = enumerations
  }

  setFilterOperators(availableFilters: TMFilterOperators): void {
    this.#filterOperators = availableFilters
  }

  getViewTasks(
    page: number,
    sort: TMSort[],
    filters: TMFilters[],
    keyword: string,
    size: number
  ): Observable<TMPaginatedResponse<TMViewTask>> {
    if (filters.length) {
      const filtersError = this.checkInvalidFilters(filters)
      if (filtersError) {
        return throwError(() => new Error(filtersError.toString()))
      }
    }

    return from(
      fetchViewTasks(
        sort,
        this.mapFilters(filters),
        keyword,
        this.#view,
        {
          page,
          size,
        },
        this.#enumerations
      )
    )
  }

  getViewTask(
    taskId: TMViewTask['id'],
    tokenUuid: TMViewTask['tokenUuid']
  ): Observable<TMViewTask> {
    return from(fetchViewTask(taskId, tokenUuid))
  }

  getAssignees(task: TMViewTask, query: { keyword: string }): Observable<TMUser[]> {
    if (query.keyword?.length < 4) {
      return throwError(() => new Error('Keyword must be at least 4 characters long'))
    }

    return from(fetchTaskAssignees(task.id, task.tokenUuid, query))
  }

  getBulkAssignees(
    tasks: { id: string; tokenUuid: string }[],
    query: { keyword: string }
  ): Observable<TMUser[]> {
    return from(fetchBulkAssignees(tasks, query))
  }

  executeTaskAction(
    action: TMExecuteTaskActions,
    task: TMViewTask,
    payload?: any
  ): Observable<void> {
    return from(updateTaskAction(action, task.id, task.tokenUuid, payload))
  }

  getTaskComments(taskId: TMViewTask['id']): Observable<TMTaskComment[]> {
    return from(fetchTaskComments(taskId))
  }

  getTaskHistory(taskId: TMViewTask['id']): Observable<TMTaskHistory[]> {
    return from(fetchTaskHistory(taskId))
  }

  addComment(taskId: TMViewTask['id'], comment: { message: string }): Observable<TMTaskComment> {
    return from(saveComment(taskId, comment))
  }

  deleteComment(taskId: TMViewTask['id'], commentId: TMTaskComment['commentId']): Observable<void> {
    return from(removeComment(taskId, commentId))
  }

  updatePriority(taskId: TMViewTask['id'], priority: number): Observable<void> {
    return from(updateTaskPriority(taskId, priority))
  }

  bulkUpdate(
    tasks: { id: TMViewTask['id']; tokenUuid: string }[],
    action: TMExecuteTaskActions,
    username: string
  ): Observable<void> {
    return from(updateBulkTaskAction(tasks, action, username))
  }

  initSse(): void {
    this.closeSseConnection()

    this.#sseConnection = initSseConnection(this.getView().resourceId, (event) =>
      this.#sseEvents.next(event)
    )
  }

  closeSseConnection(): void {
    try {
      this.#sseConnection?.close()
    } catch (error) {
      // close() aborts the underlying request; an in-flight connect surfaces
      // that as an AbortError, which is expected when closing deliberately.
      if ((error as Error)?.name !== 'AbortError') {
        throw error
      }
    }
  }

  getSseEvents(): Observable<MessageEvent<string>> {
    return this.#sseEvents.asObservable()
  }

  private checkInvalidFilters(filters: TMFilters[]): boolean | string {
    for (const filter of filters) {
      const { keyName, operator, value } = filter

      if (!this.#filterOperators[keyName]) {
        return FILTER_ERRORS.invalidKey
      }

      if (!this.#filterOperators[keyName].operators.includes(operator)) {
        return FILTER_ERRORS.invalidOperator
      }

      if (!value.length) {
        return FILTER_ERRORS.minLength
      }

      if (![Operators.inRange, Operators.in].includes(filter.operator) && value.length > 1) {
        return FILTER_ERRORS.maxLength
      }

      if (filter.operator === Operators.inRange && value.length !== 2) {
        return FILTER_ERRORS.rangeLength
      }

      const isValidValue = value.every((val) => {
        switch (typeof val) {
          case 'number':
            return FILTER_TYPES.numberTypeFilters.includes(this.#filterOperators[keyName].type)
          case 'string':
            return FILTER_TYPES.stringTypeFilters.includes(this.#filterOperators[keyName].type)
          case 'boolean':
            return FILTER_TYPES.booleanTypeFilters.includes(this.#filterOperators[keyName].type)
          default:
            return false
        }
      })

      if (!isValidValue) {
        return FILTER_ERRORS.invalidValue
      }

      const isInOptions = value.every(
        (val) =>
          !this.#filterOperators[keyName].options ||
          this.#filterOperators[keyName].options.some((option) => option.value === val)
      )

      if (!isInOptions) {
        return FILTER_ERRORS.invalidValue
      }
    }

    return false
  }

  private mapFilters(filters: TMFilters[]): ViewTasksFilter[] {
    return filters.map(({ keyName, operator, value }) => ({
      keyName,
      operator,
      value,
      keyType: this.#filterOperators[keyName].type,
      columnType: this.#view.columns.find((column) => column.keyName === keyName).columnType,
    }))
  }
}
